<script lang="ts">
	/**
	 * Pointer mechanics for a drag-to-reorder list, and nothing else (§14).
	 *
	 * It owns **where the pointer is**, not what the order means: the drop is reported as "this row
	 * was dropped onto that one" and the caller works out the new order from the list it already
	 * holds. That division is deliberate — the two screens using this arrange different things
	 * (`ProductFamily.displayOrder`, which is presentation, and `Product.rank`, which is §9.3's
	 * upgrade rule), and a component that computed orders would have to know which.
	 *
	 * **Delegation rather than per-row handlers.** Flowbite's `TableBodyRow` spreads attributes onto
	 * its `<tr>` but forwards only `click`, `contextmenu` and `dblclick`, so `on:dragstart` on the
	 * component would silently never fire. Drag events bubble, so one listener on the wrapper sees
	 * every row — and the rows stay ordinary markup carrying a `data-sort-key`.
	 *
	 * **A drag never crosses a group.** Rows may carry `data-sort-group`; a drop onto a row in
	 * another group is ignored and never indicated. That is the same rule the server enforces on
	 * `reorderProducts`: a product dragged into another line has *moved lines*, which changes what
	 * it can be upgraded from and which trial it consumes — a decision that belongs to the product
	 * form, not to a slip of the wrist.
	 */

	import { createEventDispatcher } from 'svelte';

	/** No dragging while a save is in flight: the answer would land on a list that had moved. */
	export let disabled: boolean = false;

	const dispatcher = createEventDispatcher<{ reorder: { key: string; targetKey: string } }>();

	/** The row being dragged, and the row under the pointer — exposed for the caller's styling. */
	let draggingKey: string | null = null;
	let overKey: string | null = null;

	function rowOf(event: Event): HTMLElement | null {
		const target = event.target as HTMLElement | null;
		// `closest` is absent on a text node target and in a few jsdom paths, so it is asked for
		// rather than assumed — a thrown TypeError inside a dragover handler is invisible.
		return typeof target?.closest === 'function' ? target.closest('[data-sort-key]') : null;
	}

	function keyOf(event: Event): string | null {
		return rowOf(event)?.getAttribute('data-sort-key') ?? null;
	}

	function groupOf(event: Event): string | null {
		return rowOf(event)?.getAttribute('data-sort-group') ?? null;
	}

	let draggingGroup: string | null = null;

	function fnStart(event: DragEvent): void {
		if (disabled) return;
		draggingKey = keyOf(event);
		draggingGroup = groupOf(event);
		// Some browsers refuse to start a drag without data on the transfer. The payload is
		// deliberately inert: the key we act on is the one held above, never one a page could
		// have put on the clipboard.
		event.dataTransfer?.setData('text/plain', draggingKey ?? '');
		if (event.dataTransfer !== null && event.dataTransfer !== undefined) {
			event.dataTransfer.effectAllowed = 'move';
		}
	}

	function fnOver(event: DragEvent): void {
		if (draggingKey === null) return;

		const key = keyOf(event);
		if (key === null || groupOf(event) !== draggingGroup) {
			overKey = null;
			return;
		}

		// **Required.** The browser's default for dragover is "this is not a drop target", so
		// without this the drop event never fires at all — and the row simply springs back with no
		// error anywhere.
		event.preventDefault();
		overKey = key === draggingKey ? null : key;
	}

	function fnDrop(event: DragEvent): void {
		event.preventDefault();
		const key = draggingKey;
		const targetKey = keyOf(event);
		fnEnd();

		if (key === null || targetKey === null || key === targetKey) return;
		if (groupOf(event) !== draggingGroupAt(key)) return;

		dispatcher('reorder', { key, targetKey });
	}

	/**
	 * The group the dragged row belongs to, read back from the DOM at drop time rather than from
	 * the value stashed at dragstart — the list may have re-rendered in between, and a stale group
	 * is how a cross-line drop gets through the one guard meant to stop it.
	 */
	function draggingGroupAt(key: string): string | null {
		const row = document.querySelector(`[data-sort-key="${CSS.escape(key)}"]`);
		return row?.getAttribute('data-sort-group') ?? null;
	}

	function fnEnd(): void {
		draggingKey = null;
		draggingGroup = null;
		overKey = null;
	}
</script>

<div
	on:dragstart={fnStart}
	on:dragover={fnOver}
	on:drop={fnDrop}
	on:dragend={fnEnd}
	on:dragleave={() => (overKey = null)}
>
	<slot {draggingKey} {overKey} />
</div>
